import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Quote, Star } from "lucide-react";
import { Card, CardContent } from "./ui/card";

const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const testimonials = [
    {
      quote:
        "The Automated Renaming alone saves us 10+ hours every week. No more IMG-WA files cluttering our drives. Every document is perfectly named and filed before we even see it.",
      author: "CA Rajesh Mehta",
      role: "Partner, Mehta & Associates",
      location: "Mumbai",
      highlight: "Automated Renaming",
    },
    {
      quote:
        "Data Privacy on Google Drive was our biggest concern. The fact that nothing touches third-party servers gave us the confidence to implement this across all our clients. Complete peace of mind.",
      author: "CA Priya Sharma",
      role: "Managing Partner, Sharma CA Firm",
      location: "Delhi NCR",
      highlight: "Data Privacy on Google Drive",
    },
    {
      quote:
        "We've finally ended the Chasing Culture in our firm. Clients send documents on WhatsApp, they get auto-acknowledged, and we have a complete audit trail. No more frantic calls before deadlines.",
      author: "CA Vikram Patel",
      role: "Senior Partner, Patel & Co.",
      location: "Ahmedabad",
      highlight: "Ending the Chasing Culture",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section
      id="testimonials"
      ref={ref}
      className="py-20 md:py-32 bg-cream relative overflow-hidden"
      data-testid="testimonials-section"
    >
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-navy/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <p className="font-body text-sm font-semibold uppercase tracking-widest text-gold mb-4">
            Real Results
          </p>
          <h2
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight tracking-tight mb-6"
            data-testid="testimonials-headline"
          >
            What CA Firms Are Saying
          </h2>
          <p className="font-body text-base md:text-lg text-slate-muted max-w-2xl mx-auto">
            Join the growing community of CA firms who've transformed their 
            document management and reclaimed their peace of mind.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid md:grid-cols-3 gap-8"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div key={index} variants={itemVariants}>
              <Card
                className="premium-card h-full bg-white border-black/5"
                data-testid={`testimonial-card-${index}`}
              >
                <CardContent className="p-6 md:p-8">
                  {/* Quote Icon */}
                  <div className="mb-6">
                    <Quote size={32} className="text-gold/40" />
                  </div>

                  {/* Highlight Badge */}
                  <div className="inline-block bg-navy/5 rounded-full px-3 py-1 mb-4">
                    <p className="font-body text-xs font-semibold text-navy uppercase tracking-wide">
                      {testimonial.highlight}
                    </p>
                  </div>

                  {/* Quote */}
                  <p className="font-body text-slate-main leading-relaxed mb-6 text-base">
                    "{testimonial.quote}"
                  </p>

                  {/* Rating */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        className="fill-gold text-gold"
                      />
                    ))}
                  </div>

                  {/* Author */}
                  <div className="pt-4 border-t border-black/5">
                    <p className="font-heading text-base font-bold text-navy">
                      {testimonial.author}
                    </p>
                    <p className="font-body text-sm text-slate-muted">
                      {testimonial.role}
                    </p>
                    <p className="font-body text-xs text-slate-muted mt-1">
                      {testimonial.location}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
